import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getUrlParams, randomString } from '../common';
import Logo from '../assets/Logo.svg';
import Input from './Input';
import { isValidQldMobile, normalizeQldMobile } from '../qldContact';
import Dropdown from './Dropdown';
import Checkbox from './Checkbox';
import MyDropzone from './MyDropzone';
import { clearPageMessage, showLoadingMessage, showSuccessMessage, showWarningMessage } from '../successMessage';

const eoiModes = ['Option 1 - QLD Department of Transport and Main Roads', 'Option 2 - Category A & B combination', 'Option 3 - Special arrangements'];
const tmrCardOptions = [
    'QLD Driver licence',
    'QLD Marine licence indicator',
    'QLD Photo identification card',
    'QLD Industry authority',
    'QLD Adult proof of age card',
];
const comboOptions = [
    'One Document from Category A & Two from Category B',
    'Two Documents from Category A & One from Category B',
];
const categoryAOptions = [
    'Australian Birth Certificate',
    'Bicentennial Birth Certificate',
    'Australian Citizenship Certificate or Naturalisation Certificate',
    'Queensland or Australian Federal Police Officer photo identity card',
    'Queensland Photo Identification Card or laminated Queensland 18+ Card (issued after 1 January 1982)',
    'Department of Home Affairs or Department of Immigration and Border Protection ImmiCard (not expired more than five years)',
    'Australian passport',
    'Overseas passport',
    'Australian Certificate of Identity, Document of Identity, Titre de Voyage, Document de Voyage',
    'Australian photo driver licence',
    'Queensland Government photographic high risk work licence',
    'Queensland Accreditation: driver/rider trainer; pilot/escort vehicle driver; dangerous goods driver; tow truck driver/assistant; traffic controller; passenger transport driver.',
    'Australian Government Temporary or Resident Visa or Document for travel to Australia',
];
const categoryBOptions = [
    'Australian Defence Force photo identification card (excluding civilians)',
    'Australian Firearm Licence (with photo)',
    'Australian Security Guard or Crowd Controller Licence (with photo)',
    'Australian educational institution student identity document (must include photo)',
    'Services Australia BasicsCard',
    'Interstate government-issued or government-approved Proof of Age Card, Photo Card or Photo Identification Card',
    'Department of Veterans’ Affairs or Centrelink Pensioner Concession Card (including Healthcare card)',
    'Medicare card',
    'Department of Corrective Services – Form 22 Supporting Document to Identify a Person',
    'Norfolk Island Photo ID Card',
    'Debit or credit card (must include embossed or printed name)',
];
const changeNameOptions = [
    'Australian Marriage Certificate',
    'Australian civil partnership/relationship certificate',
    'Australian Change of Name Certificate',
    'Overseas Marriage Certificate',
    'Divorce papers, Decree Nisi or Absolute',
    'Deed Poll issued before 1 February 2004',
];
const specialArrangementOptions = [
    'Aboriginal and Torres Strait Island Peoples',
    'Secondary school students',
    'Temporary overseas workers',
    'New Zealand citizens',
];
const schoolVerificationOptions = [
    'Original document plus school letterhead statement',
    'At least one Category A or B document plus school student ID card',
];
const schoolOriginalOptions = [
    'Australian birth certificate issued by the Registry of Births, Deaths and Marriages',
    'Australian citizenship certificate',
    'International travel documents including a current passport or a passport that has expired but has not been cancelled within the preceding two years',
];
const schoolOfficialOptions = [
    'principal or deputy principal',
    'head teacher or deputy head teacher',
    'secretary or deputy secretary',
    'chief administrator or deputy chief administrator',
];
const overseasSupportOptions = [
    'overseas drivers’ licence',
    'overseas financial institution card',
    'overseas birth certificate',
];

const tableStyles = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '16px',
};

const cellStyles = {
    border: '1px solid #d9d9d9',
    padding: '10px 12px',
    verticalAlign: 'top',
    textAlign: 'left',
};

const selectableCardStyles = {
    background: '#fff',
    border: '2px solid #c7d2e5',
    borderRadius: '12px',
    padding: '16px',
};

const optionPanelStyles = {
    border: '1px solid #d6d6d6',
    borderRadius: '14px',
    overflow: 'hidden',
    background: '#fff',
};

const optionPanelHeaderStyles = {
    background: '#f2f2f2',
    padding: '14px 16px',
    borderBottom: '1px solid #d6d6d6',
};

const numberBadgeStyles = {
    width: '28px',
    height: '28px',
    borderRadius: '999px',
    background: '#1f3c88',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '15px',
    flexShrink: 0,
};

const selectableBadgeStyles = {
    ...numberBadgeStyles,
    background: '#fff',
    color: '#1f3c88',
    border: '2px solid #1f3c88',
};

const subtleLabelStyles = {
    fontWeight: '400',
    fontSize: '17px',
    marginTop: '20px',
    marginBottom: '10px',
    display: 'flex',
};

const normalSectionHeadingStyles = {
    fontWeight: '400',
    fontSize: '17px',
    margin: 0,
};

const sectionHeadingStyles = {
    display: 'flex',
    fontWeight: '700',
    fontSize: '17px',
    marginTop: '15px',
    marginBottom: 0,
    color: '#111',
};

const secondaryLabelStyles = {
    fontWeight: '400',
    fontSize: '17px',
    color: '#000',
};

const sectionInputLabelStyles = {
    ...sectionHeadingStyles,
    marginTop: '15px',
};

const declarationHeadingStyles = {
    ...sectionHeadingStyles,
    marginBottom: '20px',
};

const compactSectionHeadingStyles = {
    ...sectionHeadingStyles,
    fontSize: '16px',
};

const areEqual = (left, right) => JSON.stringify(left || null) === JSON.stringify(right || null);

const buildComparableEoiPayload = ({
    trainerId = '',
    eoiMode = '',
    tmrCardType = '',
    tmrCardNumber = '',
    tmrCardExpiry = '',
    combo = '',
    documents = [],
    hasChangedName = false,
    changeNameDocumentType = '',
    changeNameUpload = '',
    specialArrangementType = '',
    specialArrangementNotes = '',
    schoolVerificationMode = '',
    schoolOriginalDocument = '',
    schoolOfficial = '',
    temporaryWorkerSupportDoc = '',
    nzSupportDocOne = '',
    nzSupportDocTwo = '',
    infoCorrect = false,
    trainerSighted = false,
    declarationDate = '',
    trainerVerificationChecks = {},
}) => ({
    trainerId,
    eoiMode: textOrValue(eoiMode),
    tmrIssuedCard: textOrValue(eoiMode) === eoiModes[0] ? {
        cardType: textOrValue(tmrCardType),
        documentNumber: tmrCardNumber,
        expiryDate: tmrCardExpiry,
    } : null,
    documentCombination: textOrValue(eoiMode) === eoiModes[1] ? textOrValue(combo) : '',
    documents: textOrValue(eoiMode) === eoiModes[1] ? documents.map((row) => ({
        category: row.category,
        documentType: textOrValue(row.documentType),
        documentNumber: row.documentNumber,
        expiryDate: row.expiryDate,
        issuingState: row.issuingState,
        issuingCountry: row.issuingCountry,
        financialInstitution: row.financialInstitution,
        financialCardType: textOrValue(row.financialCardType),
    })) : [],
    specialArrangement: textOrValue(eoiMode) === eoiModes[2] ? {
        type: textOrValue(specialArrangementType),
        notes: specialArrangementNotes,
        schoolVerificationMode: textOrValue(schoolVerificationMode),
        schoolOriginalDocument: textOrValue(schoolOriginalDocument),
        schoolOfficial: textOrValue(schoolOfficial),
        temporaryWorkerSupportDoc: textOrValue(temporaryWorkerSupportDoc),
        nzSupportDocOne: textOrValue(nzSupportDocOne),
        nzSupportDocTwo: textOrValue(nzSupportDocTwo),
    } : null,
    changeOfName: hasChangedName ? {
        documentType: textOrValue(changeNameDocumentType),
        upload: changeNameUpload,
    } : null,
    infoCorrect,
    trainerSighted,
    declarationDate,
    trainerVerificationChecks,
});

const textOrValue = (value) => (value && typeof value === 'object' ? value.value : value);
const selectValue = (value) => {
    const normalizedValue = textOrValue(value);
    return normalizedValue ? { label: normalizedValue, value: normalizedValue } : '';
};
const hasTextValue = (value) => Boolean(textOrValue(value)?.toString().trim());
const twoYearsAgo = () => {
    const nextValue = new Date();
    nextValue.setFullYear(nextValue.getFullYear() - 2);
    return nextValue;
};
const fiveYearsAgo = () => {
    const nextValue = new Date();
    nextValue.setFullYear(nextValue.getFullYear() - 5);
    return nextValue;
};
const sixMonthsAgo = () => {
    const nextValue = new Date();
    nextValue.setMonth(nextValue.getMonth() - 6);
    return nextValue;
};

const getDocumentExpiryRule = (documentType = '', category = '') => {
    if (!documentType) {
        return { required: true, valid: true, helpText: '' };
    }

    if (category === 'B') {
        if (documentType.includes('Form 22')) {
            return { required: true, valid: (value) => new Date(value) >= sixMonthsAgo(), helpText: 'Must be issued within the last 6 months.' };
        }

        return { required: true, valid: (value) => new Date(value) >= new Date(), helpText: 'Must be current.' };
    }

    if (
        documentType.includes('Birth Certificate')
        || documentType.includes('Citizenship Certificate')
        || documentType.includes('Police Officer')
        || documentType.includes('Queensland Photo Identification Card or laminated Queensland 18+ Card')
    ) {
        return { required: false, valid: true, helpText: 'No expiry requirement applies to this document type.' };
    }

    if (documentType.includes('ImmiCard')) {
        return { required: true, valid: (value) => new Date(value) >= fiveYearsAgo(), helpText: 'Must not be expired by more than 5 years.' };
    }

    if (documentType.includes('Temporary or Resident Visa') || documentType.includes('Document for travel to Australia')) {
        return { required: true, valid: (value) => new Date(value) >= fiveYearsAgo(), helpText: 'Must be valid for up to 5 years after issue.' };
    }

    return { required: true, valid: (value) => new Date(value) >= twoYearsAgo(), helpText: 'Must be current or expired less than 2 years ago.' };
};

const createDocument = (category = '') => ({
    category,
    documentType: '',
    documentNumber: '',
    expiryDate: '',
    issuingState: '',
    issuingCountry: '',
    financialInstitution: '',
    financialCardType: '',
});

const QldEoiForm = ({ embedded = false, onComplete, onProgress, initialData = {}, trainerIdOverride = '', trainerMode = false, hideTrainerDecisionActions = false, studentNameOverride = '', mobileOverride = '' }) => {
    const [mobile, setMobile] = useState(mobileOverride);
    useEffect(() => { setMobile(mobileOverride); }, [mobileOverride]);
    const navigate = useNavigate();

    const [errors, setErrors] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [submitState, setSubmitState] = useState('idle');
    const [trainerId, setTrainerId] = useState(initialData.trainerId || trainerIdOverride || '');

    const [eoiMode, setEoiMode] = useState(initialData.eoiMode || '');
    const [tmrCardType, setTmrCardType] = useState(initialData.tmrIssuedCard?.cardType || '');
    const [tmrCardNumber, setTmrCardNumber] = useState(initialData.tmrIssuedCard?.documentNumber || '');
    const [tmrCardExpiry, setTmrCardExpiry] = useState(initialData.tmrIssuedCard?.expiryDate || '');
    const [combo, setCombo] = useState(initialData.documentCombination || '');
    const [documents, setDocuments] = useState(initialData.documents?.length ? initialData.documents.map((row) => ({ ...createDocument(), ...row })) : [createDocument(), createDocument(), createDocument()]);

    const [hasChangedName, setHasChangedName] = useState(Boolean(initialData.changeOfName));
    const [changeNameDocumentType, setChangeNameDocumentType] = useState(initialData.changeOfName?.documentType || '');
    const [changeNameUpload, setChangeNameUpload] = useState(initialData.changeOfName?.upload || '');

    const [specialArrangementType, setSpecialArrangementType] = useState(initialData.specialArrangement?.type || '');
    const [specialArrangementNotes, setSpecialArrangementNotes] = useState(initialData.specialArrangement?.notes || '');
    const [schoolVerificationMode, setSchoolVerificationMode] = useState(initialData.specialArrangement?.schoolVerificationMode || '');
    const [schoolOriginalDocument, setSchoolOriginalDocument] = useState(initialData.specialArrangement?.schoolOriginalDocument || '');
    const [schoolOfficial, setSchoolOfficial] = useState(initialData.specialArrangement?.schoolOfficial || '');
    const [temporaryWorkerSupportDoc, setTemporaryWorkerSupportDoc] = useState(initialData.specialArrangement?.temporaryWorkerSupportDoc || '');
    const [nzSupportDocOne, setNzSupportDocOne] = useState(initialData.specialArrangement?.nzSupportDocOne || '');
    const [nzSupportDocTwo, setNzSupportDocTwo] = useState(initialData.specialArrangement?.nzSupportDocTwo || '');

    const [infoCorrect, setInfoCorrect] = useState(Boolean(initialData.infoCorrect));
    const [trainerSighted, setTrainerSighted] = useState(Boolean(initialData.trainerSighted));
    const [declarationDate, setDeclarationDate] = useState(initialData.declarationDate || format(new Date(), 'dd-MM-yyyy'));
    const [trainerVerificationChecks, setTrainerVerificationChecks] = useState(initialData.trainerVerificationChecks || {
        originalSighted: false,
        documentStatusChecked: false,
        identityMatched: false,
    });

    const currentPayload = useMemo(() => buildComparableEoiPayload({
        trainerId,
        eoiMode,
        tmrCardType,
        tmrCardNumber,
        tmrCardExpiry,
        combo,
        documents,
        hasChangedName,
        changeNameDocumentType,
        changeNameUpload,
        specialArrangementType,
        specialArrangementNotes,
        schoolVerificationMode,
        schoolOriginalDocument,
        schoolOfficial,
        temporaryWorkerSupportDoc,
        nzSupportDocOne,
        nzSupportDocTwo,
        infoCorrect,
        trainerSighted,
        declarationDate,
        trainerVerificationChecks,
    }), [
        trainerId,
        eoiMode,
        tmrCardType,
        tmrCardNumber,
        tmrCardExpiry,
        combo,
        documents,
        hasChangedName,
        changeNameDocumentType,
        changeNameUpload,
        specialArrangementType,
        specialArrangementNotes,
        schoolVerificationMode,
        schoolOriginalDocument,
        schoolOfficial,
        temporaryWorkerSupportDoc,
        nzSupportDocOne,
        nzSupportDocTwo,
        infoCorrect,
        trainerSighted,
        declarationDate,
        trainerVerificationChecks,
    ]);

    useEffect(() => {
        const nextPayload = buildComparableEoiPayload({
            trainerId: initialData.trainerId || trainerIdOverride || '',
            eoiMode: initialData.eoiMode || '',
            tmrCardType: initialData.tmrIssuedCard?.cardType || '',
            tmrCardNumber: initialData.tmrIssuedCard?.documentNumber || '',
            tmrCardExpiry: initialData.tmrIssuedCard?.expiryDate || '',
            combo: initialData.documentCombination || '',
            documents: initialData.documents?.length ? initialData.documents.map((row) => ({ ...createDocument(), ...row })) : [createDocument(), createDocument(), createDocument()],
            hasChangedName: Boolean(initialData.changeOfName),
            changeNameDocumentType: initialData.changeOfName?.documentType || '',
            changeNameUpload: initialData.changeOfName?.upload || '',
            specialArrangementType: initialData.specialArrangement?.type || '',
            specialArrangementNotes: initialData.specialArrangement?.notes || '',
            schoolVerificationMode: initialData.specialArrangement?.schoolVerificationMode || '',
            schoolOriginalDocument: initialData.specialArrangement?.schoolOriginalDocument || '',
            schoolOfficial: initialData.specialArrangement?.schoolOfficial || '',
            temporaryWorkerSupportDoc: initialData.specialArrangement?.temporaryWorkerSupportDoc || '',
            nzSupportDocOne: initialData.specialArrangement?.nzSupportDocOne || '',
            nzSupportDocTwo: initialData.specialArrangement?.nzSupportDocTwo || '',
            infoCorrect: Boolean(initialData.infoCorrect),
            trainerSighted: Boolean(initialData.trainerSighted),
            declarationDate: initialData.declarationDate || format(new Date(), 'dd-MM-yyyy'),
            trainerVerificationChecks: initialData.trainerVerificationChecks || {
                originalSighted: false,
                documentStatusChecked: false,
                identityMatched: false,
            },
        });

        if (areEqual(nextPayload, currentPayload)) {
            return;
        }

        setTrainerId(initialData.trainerId || trainerIdOverride || '');
        setEoiMode(initialData.eoiMode || '');
        setTmrCardType(initialData.tmrIssuedCard?.cardType || '');
        setTmrCardNumber(initialData.tmrIssuedCard?.documentNumber || '');
        setTmrCardExpiry(initialData.tmrIssuedCard?.expiryDate || '');
        setCombo(initialData.documentCombination || '');
        setDocuments(initialData.documents?.length ? initialData.documents.map((row) => ({ ...createDocument(), ...row })) : [createDocument(), createDocument(), createDocument()]);
        setHasChangedName(Boolean(initialData.changeOfName));
        setChangeNameDocumentType(initialData.changeOfName?.documentType || '');
        setChangeNameUpload(initialData.changeOfName?.upload || '');
        setSpecialArrangementType(initialData.specialArrangement?.type || '');
        setSpecialArrangementNotes(initialData.specialArrangement?.notes || '');
        setSchoolVerificationMode(initialData.specialArrangement?.schoolVerificationMode || '');
        setSchoolOriginalDocument(initialData.specialArrangement?.schoolOriginalDocument || '');
        setSchoolOfficial(initialData.specialArrangement?.schoolOfficial || '');
        setTemporaryWorkerSupportDoc(initialData.specialArrangement?.temporaryWorkerSupportDoc || '');
        setNzSupportDocOne(initialData.specialArrangement?.nzSupportDocOne || '');
        setNzSupportDocTwo(initialData.specialArrangement?.nzSupportDocTwo || '');
        setInfoCorrect(Boolean(initialData.infoCorrect));
        setTrainerSighted(Boolean(initialData.trainerSighted));
        setDeclarationDate(initialData.declarationDate || format(new Date(), 'dd-MM-yyyy'));
        setTrainerVerificationChecks(initialData.trainerVerificationChecks || {
            originalSighted: false,
            documentStatusChecked: false,
            identityMatched: false,
        });
    }, [initialData, trainerIdOverride]);

    useEffect(() => {
        setSubmitState('idle');
    }, [
        trainerId,
        eoiMode,
        tmrCardType,
        tmrCardNumber,
        tmrCardExpiry,
        combo,
        documents,
        hasChangedName,
        changeNameDocumentType,
        changeNameUpload,
        specialArrangementType,
        specialArrangementNotes,
        schoolVerificationMode,
        schoolOriginalDocument,
        schoolOfficial,
        temporaryWorkerSupportDoc,
        nzSupportDocOne,
        nzSupportDocTwo,
        infoCorrect,
        trainerSighted,
        declarationDate,
        trainerVerificationChecks,
    ]);

    useEffect(() => {
        if (trainerIdOverride) {
            setTrainerId(trainerIdOverride);
            return;
        }

        const params = getUrlParams();
        if (params.trainerId || params.trainerid) {
            setTrainerId(params.trainerId || params.trainerid);
            return;
        }

        if (!initialData.trainerId) {
            toast.error('Trainer ID is required!!!', { position: 'top-center' });
        }
    }, [trainerIdOverride, initialData.trainerId]);

    const documentCategories = useMemo(() => {
        if (textOrValue(combo) === comboOptions[0]) {
            return ['A', 'B', 'B'];
        }

        if (textOrValue(combo) === comboOptions[1]) {
            return ['A', 'A', 'B'];
        }

        return ['', '', ''];
    }, [combo]);

    useEffect(() => {
        setDocuments((previous) => previous.map((row, index) => ({ ...row, category: documentCategories[index] })));
    }, [documentCategories]);

    const buildPayload = () => ({ ...currentPayload, mobile: normalizeQldMobile(mobile) });

    useEffect(() => {
        if (!onProgress || (trainerMode && !isValidQldMobile(mobile))) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            onProgress(buildPayload());
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [
        onProgress,
        mobile,
        trainerMode,
        trainerId,
        eoiMode,
        tmrCardType,
        tmrCardNumber,
        tmrCardExpiry,
        combo,
        documents,
        hasChangedName,
        changeNameDocumentType,
        changeNameUpload,
        specialArrangementType,
        specialArrangementNotes,
        schoolVerificationMode,
        schoolOriginalDocument,
        schoolOfficial,
        temporaryWorkerSupportDoc,
        nzSupportDocOne,
        nzSupportDocTwo,
        infoCorrect,
        trainerSighted,
        declarationDate,
        trainerVerificationChecks,
    ]);

    const setDocumentField = (index, field, value) => {
        setDocuments((previous) => previous.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)));
    };

    const getAvailableDocumentOptions = (index, category) => {
        const sourceOptions = category === 'A' ? categoryAOptions : categoryBOptions;
        const selectedInCategory = new Set(
            documents
                .filter((row, rowIndex) => rowIndex !== index && row.category === category)
                .map((row) => textOrValue(row.documentType))
                .filter(Boolean)
        );

        return sourceOptions.filter((option) => (
            option === textOrValue(documents[index]?.documentType) || !selectedInCategory.has(option)
        ));
    };

    const clearFieldErrors = (...fieldNames) => {
        setErrors((previous) => previous.filter((field) => !fieldNames.includes(field)));
        setErrorMessage('');
    };

    const validateTmrIssuedCard = () => {
        const tempErrors = [];
        const expiryRule = getDocumentExpiryRule(textOrValue(tmrCardType), 'A');

        if (!hasTextValue(tmrCardType)) {
            tempErrors.push('tmrCardType');
        }

        if (!tmrCardNumber.trim()) {
            tempErrors.push('tmrCardNumber');
        }

        if (!tmrCardExpiry || (typeof expiryRule.valid === 'function' && !expiryRule.valid(tmrCardExpiry))) {
            tempErrors.push('tmrCardExpiry');
        }

        if (tempErrors.length > 0) {
            setErrors(tempErrors);
            setErrorMessage(
                tempErrors.includes('tmrCardExpiry')
                    ? 'Document expired, choose another document.'
                    : 'Please complete the Queensland-issued card fields.'
            );
            return false;
        }

        return true;
    };

    const validateThreeDocuments = () => {
        if (!hasTextValue(combo)) {
            setErrors(['combo']);
            setErrorMessage('Please choose the document combination.');
            return false;
        }

        const duplicateTracker = {};

        for (let index = 0; index < documents.length; index += 1) {
            const row = documents[index];
            const docType = textOrValue(row.documentType);
            const expiryRule = getDocumentExpiryRule(docType, row.category);
            const documentTypeField = `document-type-${index}`;
            const documentNumberField = `document-number-${index}`;
            const documentExpiryField = `document-expiry-${index}`;
            const documentRowField = `document-${index}`;
            const financialInstitutionField = `financialInstitution-${index}`;
            const financialCardTypeField = `financialCardType-${index}`;

            if (!docType) {
                setErrors([documentTypeField]);
                setErrorMessage(`Please complete document ${index + 1}.`);
                return false;
            }

            if (!row.documentNumber?.trim()) {
                setErrors([documentNumberField]);
                setErrorMessage(`Please complete document ${index + 1}.`);
                return false;
            }

            const duplicateKey = `${row.category}-${docType}`;
            if (duplicateTracker[duplicateKey]) {
                setErrors([documentTypeField]);
                setErrorMessage('The same document type cannot be selected twice in the same category.');
                return false;
            }
            duplicateTracker[duplicateKey] = true;

            if (expiryRule.required && !row.expiryDate) {
                setErrors([documentExpiryField]);
                setErrorMessage(`Please provide the expiry date for document ${index + 1}.`);
                return false;
            }

            if (row.expiryDate && typeof expiryRule.valid === 'function' && !expiryRule.valid(row.expiryDate)) {
                setErrors([documentExpiryField]);
                setErrorMessage(`Document ${index + 1} does not meet the required expiry status.`);
                return false;
            }

            if (docType === 'Debit or credit card (must include embossed or printed name)') {
                if (!row.financialInstitution?.trim() || !hasTextValue(row.financialCardType) || !row.expiryDate) {
                    const financialErrors = [];
                    if (!row.financialInstitution?.trim()) {
                        financialErrors.push(financialInstitutionField);
                    }
                    if (!hasTextValue(row.financialCardType)) {
                        financialErrors.push(financialCardTypeField);
                    }
                    if (!row.expiryDate) {
                        financialErrors.push(documentExpiryField);
                    }
                    setErrors(financialErrors.length > 0 ? financialErrors : [documentRowField]);
                    setErrorMessage(`Please complete the financial card details for document ${index + 1}.`);
                    return false;
                }
            }
        }

        return true;
    };

    const validateSpecialArrangements = () => {
        const type = textOrValue(specialArrangementType);

        if (!type) {
            setErrors(['specialArrangementType']);
            setErrorMessage('Please choose the special arrangement type.');
            return false;
        }

        if (type === 'Secondary school students' && !hasTextValue(schoolVerificationMode)) {
            setErrors(['schoolVerificationMode']);
            setErrorMessage('Please choose the school student verification method.');
            return false;
        }

        if (textOrValue(schoolVerificationMode) === schoolVerificationOptions[0]) {
            if (!hasTextValue(schoolOriginalDocument) || !hasTextValue(schoolOfficial)) {
                setErrors(['schoolOriginalDocument', 'schoolOfficial']);
                setErrorMessage('Please complete the school verification details.');
                return false;
            }
        }

        if (type === 'Temporary overseas workers' && !hasTextValue(temporaryWorkerSupportDoc)) {
            setErrors(['temporaryWorkerSupportDoc']);
            setErrorMessage('Please select the additional document type.');
            return false;
        }

        if (type === 'New Zealand citizens' && (!hasTextValue(nzSupportDocOne) || !hasTextValue(nzSupportDocTwo))) {
            setErrors(['nzSupportDocOne', 'nzSupportDocTwo']);
            setErrorMessage('Please choose both supporting document types.');
            return false;
        }

        return true;
    };

    const validateAll = () => {
        const mode = textOrValue(eoiMode);
        if (!mode) {
            setErrors(['eoiMode']);
            setErrorMessage('Please choose an evidence of identity path.');
            return false;
        }

        if (mode === eoiModes[0] && !validateTmrIssuedCard()) {
            return false;
        }

        if (mode === eoiModes[1] && !validateThreeDocuments()) {
            return false;
        }

        if (mode === eoiModes[2] && !validateSpecialArrangements()) {
            return false;
        }

        const tempErrors = [];

        if (hasChangedName) {
            if (!hasTextValue(changeNameDocumentType)) {
                tempErrors.push('changeNameDocumentType');
            }

            if (!changeNameUpload) {
                tempErrors.push('changeNameUpload');
            }
        }

        if (!infoCorrect) {
            tempErrors.push('infoCorrect');
        }

        if (!trainerSighted) {
            tempErrors.push('trainerSighted');
        }

        if (!declarationDate) {
            tempErrors.push('declarationDate');
        }

        if (tempErrors.length > 0) {
            setErrors(tempErrors);
            setErrorMessage('Please complete the declaration section.');
            return false;
        }

        setErrors([]);
        setErrorMessage('');
        return true;
    };

    const handleSubmit = async (decision = '') => {
        if (trainerMode && !isValidQldMobile(mobile)) {
            setErrorMessage('Enter a valid mobile number before saving.');
            return;
        }
        if (trainerMode && decision === 'verified' && !Object.values(trainerVerificationChecks).every(Boolean)) {
            setErrorMessage('Complete all verification checks before marking this EOI as verified.');
            return;
        }

        if (!validateAll()) {
            return;
        }

        const payload = buildPayload();
        setSubmitState('submitting');
        showLoadingMessage(trainerMode ? 'Updating EOI Review' : 'Submitting EOI');

        if (embedded && onComplete) {
            try {
                await Promise.resolve(onComplete(payload, trainerMode ? { decision } : undefined));
                setSubmitState(trainerMode ? 'saved' : 'idle');
                showSuccessMessage(trainerMode ? 'EOI review updated successfully' : 'EOI submitted successfully');
            } catch (error) {
                clearPageMessage();
                setSubmitState('idle');
                showWarningMessage('EOI could not be submitted. Please try again.');
            }
            return;
        }

        window.localStorage.setItem('qld-eoi-data', JSON.stringify(payload));
        showSuccessMessage('EOI submitted successfully');
        navigate('/thankyouQld');
    };

    const renderDocumentFields = (row, index) => {
        const options = getAvailableDocumentOptions(index, row.category);
        const docType = textOrValue(row.documentType);
        const expiryRule = getDocumentExpiryRule(docType, row.category);

        return (
            <div key={`doc-${index}`} style={{ border: '1px solid #ddd', padding: '16px', marginTop: '20px', borderRadius: '8px' }}>
                <h3>Document {index + 1} - Category {row.category}</h3>
                <Dropdown options={options} value={selectValue(row.documentType)} setValue={(value) => { setDocumentField(index, 'documentType', textOrValue(value)); clearFieldErrors(`document-type-${index}`); }} label='Document type' errorFields={errors} name={`document-type-${index}`} />
                <Input type='text' errors={errors} name={`document-number-${index}`} value={row.documentNumber} setValue={(value) => { setDocumentField(index, 'documentNumber', value); clearFieldErrors(`document-number-${index}`); }} label='Document number' errorMessage={errors.includes(`document-number-${index}`) && errors.length === 1 && errorMessage} />

                {(docType === 'Australian passport' || docType === 'Overseas passport') && (
                    <Input type='text' errors={errors} name={`document-country-${index}`} value={row.issuingCountry} setValue={(value) => setDocumentField(index, 'issuingCountry', value)} label='Country of issue' />
                )}

                {(docType === 'Australian photo driver licence' || docType?.includes('Queensland Accreditation')) && (
                    <Input type='text' errors={errors} name={`document-state-${index}`} value={row.issuingState} setValue={(value) => setDocumentField(index, 'issuingState', value)} label='Issuing state / authority' />
                )}

                {expiryRule.required && (
                    <>
                        <label className='fieldHeading' style={subtleLabelStyles}>Document expiry date</label>
                        <input className={errors.includes(`document-expiry-${index}`) ? 'error' : ''} type='date' value={row.expiryDate || ''} onChange={(event) => { setDocumentField(index, 'expiryDate', event.target.value); clearFieldErrors(`document-expiry-${index}`); }} />
                    </>
                )}
                <p style={{ margin: '4px 0 0', color: errors.includes(`document-expiry-${index}`) ? '#b42318' : '#666', fontSize: '14px' }}>
                    {errors.includes(`document-expiry-${index}`) && errors.length === 1 ? errorMessage : expiryRule.helpText}
                </p>

                {docType === 'Debit or credit card (must include embossed or printed name)' && (
                    <>
                        <Input type='text' errors={errors} name={`financialInstitution-${index}`} value={row.financialInstitution} setValue={(value) => { setDocumentField(index, 'financialInstitution', value); clearFieldErrors(`financialInstitution-${index}`); }} label='Financial institution name' />
                        <Dropdown options={['Debit card', 'Credit card']} value={selectValue(row.financialCardType)} setValue={(value) => { setDocumentField(index, 'financialCardType', textOrValue(value)); clearFieldErrors(`financialCardType-${index}`); }} label='Debit or credit card' errorFields={errors} name={`financialCardType-${index}`} />
                    </>
                )}
            </div>
        );
    };

    return (
        <div className='form-wrapper qld-branded-shell'>
            <div className='qld-page-brand'>
                <div>
                    <h1>Evidence of Identity - QLD White Card Course</h1>
                    <p>Upskill Institute must verify your identity in accordance with the course entry requirements. You have 3 options to choose from when completing this section. A photograph of an original document is not an original document. Certified photocopies are not acceptable.</p>
                </div>
                <img src={Logo} alt='Upskill Institute' />
            </div>

            {trainerMode && <Input type='text' bold disabled value={studentNameOverride} setValue={() => { }} label='Name' errors={[]} name='trainerStudentName' />}
            <Input type='tel' label='Mobile Number' name='mobile' value={mobile} setValue={setMobile}
                readOnly={!trainerMode} errors={trainerMode && !isValidQldMobile(mobile) ? ['mobile'] : []}
                errorMessage={trainerMode && !isValidQldMobile(mobile) ? 'Enter a valid mobile number (8-15 digits).' : ''} />

            <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr' }}>
                    <div style={optionPanelStyles}>
                        <div style={optionPanelHeaderStyles}>
                            <p style={{ margin: 0, fontWeight: '700' }}>Option 1 - QLD Department of Transport and Main Roads issued:</p>
                        </div>
                        <div style={{ padding: '16px' }}>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                <li>Driver licence</li>
                                <li>Marine licence indicator</li>
                                <li>Photo identification card</li>
                                <li>Industry authority</li>
                                <li>Adult proof of age card</li>
                            </ul>
                            <p style={{ marginBottom: 0 }}>EOI must be current or expired less than two years. Original may include an electronic document if the document can be accessed and viewed in real time through the Queensland Government Digital Licence application.</p>
                        </div>
                    </div>

                    <div style={optionPanelStyles}>
                        <div style={optionPanelHeaderStyles}>
                            <p style={{ margin: 0, fontWeight: '700' }}>Option 2 - EOI documents that comprise of:</p>
                        </div>
                        <div style={{ padding: '16px' }}>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                <li>One (1) Category A document and two (2) Category B documents, OR</li>
                                <li>Two (2) Category A documents and one (1) Category B document.</li>
                            </ul>
                            <p>Original may include an electronic document if the document can be accessed and viewed in real time through a secure portal (e.g. the person logs in to the myGov portal and produces their Medicare card).</p>
                            <p style={{ marginBottom: 0 }}>Upskill Institute will either keep a copy of the EOI document (except financial institution debit/credit card), or record all relevant details of the EOI on your student file. Where a financial institution debit/credit card is sighted as EOI, Upskill Institute must make a record of the name of the financial institution, whether the card is a debit or credit card, and the expiry date.</p>
                        </div>
                    </div>

                    <div style={optionPanelStyles}>
                        <div style={optionPanelHeaderStyles}>
                            <p style={{ margin: 0, fontWeight: '700' }}>Option 3 - Special arrangements:</p>
                        </div>
                        <div style={{ padding: '16px' }}>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {specialArrangementOptions.map((option) => <li key={option}>{option}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <label className='fieldHeading' style={sectionHeadingStyles}>Select one option</label>
            <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: '1fr', marginBottom: '16px' }}>
                {eoiModes.map((mode, index) => {
                    const isSelected = textOrValue(eoiMode) === mode;
                    const hasError = errors.includes('eoiMode');

                    return (
                        <button
                            key={mode}
                            type='button'
                            onClick={() => {
                                setEoiMode(mode);
                                setErrors((previous) => previous.filter((field) => field !== 'eoiMode'));
                            }}
                            style={{
                                ...selectableCardStyles,
                                cursor: 'pointer',
                                textAlign: 'left',
                                color: '#111',
                                background: isSelected ? '#e7f0ff' : '#fff',
                                border: `2px solid ${isSelected ? '#1f3c88' : hasError ? '#d32f2f' : '#c7d2e5'}`,
                                boxShadow: isSelected ? '0 10px 24px rgba(31, 60, 136, 0.16)' : '0 4px 12px rgba(15, 23, 42, 0.08)',
                                padding: '14px 16px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ ...selectableBadgeStyles, background: isSelected ? '#1f3c88' : '#fff', color: isSelected ? '#fff' : '#1f3c88', borderColor: '#1f3c88' }}>{index + 1}</span>
                                <p style={{ margin: 0 }}><strong>{mode}</strong></p>
                            </div>
                        </button>
                    );
                })}
            </div>

            {textOrValue(eoiMode) === eoiModes[0] && (
                <>
                    <p>Select one document from the list below.</p>
                    <Dropdown
                        options={tmrCardOptions}
                        value={selectValue(tmrCardType)}
                        setValue={(value) => {
                            setTmrCardType(textOrValue(value));
                            clearFieldErrors('tmrCardType');
                        }}
                        label='Document type'
                        errorFields={errors}
                        name='tmrCardType'
                    />
                    <Input type='text' errors={errors} name='tmrCardNumber' value={tmrCardNumber} setValue={(value) => { setTmrCardNumber(value); clearFieldErrors('tmrCardNumber'); }} label='Document number' errorMessage={errors.includes('tmrCardNumber') && errors.length === 1 && errorMessage} />
                    <label className='fieldHeading' style={{ ...subtleLabelStyles, fontSize: '16px', color: '#000' }}>Document expiry date</label>
                    <input className={errors.includes('tmrCardExpiry') ? 'error' : ''} type='date' value={tmrCardExpiry} onChange={(event) => { setTmrCardExpiry(event.target.value); clearFieldErrors('tmrCardExpiry'); }} />
                    <p style={{ margin: '4px 0 0', color: errors.includes('tmrCardExpiry') ? '#b42318' : '#666', fontSize: '14px' }}>{getDocumentExpiryRule(textOrValue(tmrCardType), 'A').helpText}</p>
                </>
            )}

            {textOrValue(eoiMode) === eoiModes[1] && (
                <>
                    <table style={tableStyles}>
                        <thead>
                            <tr>
                                <th style={cellStyles}>Category A Document</th>
                                <th style={cellStyles}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={cellStyles}>
                                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                        <li>Australian Birth Certificate</li>
                                        <li>Bicentennial Birth Certificate</li>
                                        <li>Australian Citizenship Certificate or Naturalisation Certificate</li>
                                        <li>Queensland or Australian Federal Police Officer photo identity card</li>
                                        <li>Queensland Photo Identification Card or laminated Queensland 18+ Card (issued after 1 January 1982)</li>
                                        <li>Department of Home Affairs or Department of Immigration and Border Protection ImmiCard (not expired more than five years)</li>
                                    </ul>
                                </td>
                                <td style={cellStyles}>Must be current, except the ImmiCard which must not be expired more than five years.</td>
                            </tr>
                            <tr>
                                <td style={cellStyles}>
                                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                        <li>Australian passport</li>
                                        <li>Overseas passport</li>
                                        <li>Australian Certificate of Identity, Document of Identity, Titre de Voyage, Document de Voyage</li>
                                        <li>Australian photo driver licence</li>
                                        <li>Queensland Government photographic high risk work licence</li>
                                        <li>Queensland Accreditation: driver/rider trainer; pilot/escort vehicle driver; dangerous goods driver; tow truck driver/assistant; traffic controller; passenger transport driver.</li>
                                    </ul>
                                </td>
                                <td style={cellStyles}>Current or expired less than two years.</td>
                            </tr>
                            <tr>
                                <td style={cellStyles}>
                                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                        <li>Australian Government Temporary or Resident Visa or Document for travel to Australia</li>
                                    </ul>
                                    <p style={{ marginBottom: 0 }}><strong>Note:</strong> Electronic Travel Authority (ETA) is not accepted.</p>
                                </td>
                                <td style={cellStyles}>Valid up to five years after issue.</td>
                            </tr>
                            <tr>
                                <th style={cellStyles}>Category B Document</th>
                                <th style={cellStyles}>Status</th>
                            </tr>
                            <tr>
                                <td style={cellStyles}>
                                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                        {categoryBOptions.map((option) => <li key={option}>{option}</li>)}
                                    </ul>
                                </td>
                                <td style={cellStyles}>Must be current. Department of Corrective Services Form 22 is accepted up to six months after issue.</td>
                            </tr>
                        </tbody>
                    </table>
                    <label className='fieldHeading' style={compactSectionHeadingStyles}>Choose one combination</label>
                    <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', marginBottom: '16px' }}>
                        {comboOptions.map((option, index) => {
                            const isSelected = textOrValue(combo) === option;
                            const hasError = errors.includes('combo');

                            return (
                                <button
                                    key={option}
                                    type='button'
                                    onClick={() => {
                                        setCombo(option);
                                        setErrors((previous) => previous.filter((field) => field !== 'combo'));
                                    }}
                                    style={{
                                        ...selectableCardStyles,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        color: '#111',
                                        background: isSelected ? '#e7f0ff' : '#fff',
                                        border: `2px solid ${isSelected ? '#1f3c88' : hasError ? '#d32f2f' : '#c7d2e5'}`,
                                        boxShadow: isSelected ? '0 10px 24px rgba(31, 60, 136, 0.16)' : '0 4px 12px rgba(15, 23, 42, 0.08)',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                        <span style={{ ...selectableBadgeStyles, background: isSelected ? '#1f3c88' : '#fff', color: isSelected ? '#fff' : '#1f3c88', borderColor: '#1f3c88' }}>{index + 1}</span>
                                        <p style={{ margin: 0 }}><strong>{option}</strong></p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    <p style={{ marginTop: 0, color: '#666' }}>The same document type cannot be selected twice in the same category.</p>
                    {documents.map((row, index) => renderDocumentFields(row, index))}
                </>
            )}

            {textOrValue(eoiMode) === eoiModes[2] && (
                <>
                    <Dropdown options={specialArrangementOptions} value={selectValue(specialArrangementType)} setValue={(value) => setSpecialArrangementType(textOrValue(value))} label='Special arrangement type' errorFields={errors} name='specialArrangementType' />

                    {textOrValue(specialArrangementType) === 'Secondary school students' && (
                        <>
                            <Dropdown options={schoolVerificationOptions} value={selectValue(schoolVerificationMode)} setValue={(value) => setSchoolVerificationMode(textOrValue(value))} label='School verification method' errorFields={errors} name='schoolVerificationMode' />
                            {textOrValue(schoolVerificationMode) === schoolVerificationOptions[0] && (
                                <>
                                    <Dropdown options={schoolOriginalOptions} value={selectValue(schoolOriginalDocument)} setValue={(value) => setSchoolOriginalDocument(textOrValue(value))} label='Original document sighted' errorFields={errors} name='schoolOriginalDocument' />
                                    <Dropdown options={schoolOfficialOptions} value={selectValue(schoolOfficial)} setValue={(value) => setSchoolOfficial(textOrValue(value))} label='School official signing the statement' errorFields={errors} name='schoolOfficial' />
                                </>
                            )}
                        </>
                    )}

                    {textOrValue(specialArrangementType) === 'Temporary overseas workers' && (
                        <Dropdown options={overseasSupportOptions} value={selectValue(temporaryWorkerSupportDoc)} setValue={(value) => setTemporaryWorkerSupportDoc(textOrValue(value))} label='Additional document' errorFields={errors} name='temporaryWorkerSupportDoc' />
                    )}

                    {textOrValue(specialArrangementType) === 'New Zealand citizens' && (
                        <>
                            <Dropdown options={overseasSupportOptions} value={selectValue(nzSupportDocOne)} setValue={(value) => setNzSupportDocOne(textOrValue(value))} label='Supporting document 1' errorFields={errors} name='nzSupportDocOne' />
                            <Dropdown options={overseasSupportOptions} value={selectValue(nzSupportDocTwo)} setValue={(value) => setNzSupportDocTwo(textOrValue(value))} label='Supporting document 2' errorFields={errors} name='nzSupportDocTwo' />
                        </>
                    )}

                    <label className='fieldHeading'>Notes</label>
                    <textarea value={specialArrangementNotes} onChange={(event) => setSpecialArrangementNotes(event.target.value)} />
                </>
            )}

            <h2 style={sectionHeadingStyles}>Change of Name</h2>
            <p style={secondaryLabelStyles}>If you have changed your name and the name on the EOI documents is different to the name you want on your GCIT card.</p>
            <Checkbox labelStyle={secondaryLabelStyles} label='The name on the EOI documents is different to the name required on the GCIT card' value={hasChangedName} setValue={setHasChangedName} />
            {hasChangedName && (
                <>
                    <Dropdown options={changeNameOptions} value={selectValue(changeNameDocumentType)} setValue={(value) => setChangeNameDocumentType(textOrValue(value))} label='Change of name document (list)' errorFields={errors} name='changeNameDocumentType' customStyle={secondaryLabelStyles} />
                    <label className='fieldHeading' style={sectionHeadingStyles}>Upload Change of name document</label>
                    <MyDropzone storageLocation={`QldEoi/ChangeOfName/${trainerId || 'unknown'}/${randomString(8)}`} customMessage='Click or drop file here to upload' editing errors={errors} name='changeNameUpload' image={changeNameUpload} updateImage={setChangeNameUpload} allowPdf />
                </>
            )}

            <h2 style={declarationHeadingStyles}>Declaration</h2>
            <Checkbox labelStyle={secondaryLabelStyles} label='I confirm the details provided are true and correct.' value={infoCorrect} setValue={setInfoCorrect} errors={errors} name='infoCorrect' />
            <Checkbox labelStyle={secondaryLabelStyles} label='I understand an Upskill Institute trainer must sight the original EOI documents.' value={trainerSighted} setValue={setTrainerSighted} errors={errors} name='trainerSighted' />
            <div style={{ marginTop: '15px' }}>
            <Input type='text' disabled errors={errors} name='declarationDate' value={declarationDate} setValue={setDeclarationDate} label='Declaration date' labelStyle={sectionInputLabelStyles} />
            </div>

            {trainerMode && (
                <>
                    <h2>Verified with</h2>
                    <Checkbox label='Original EOI documents sighted' value={trainerVerificationChecks.originalSighted} setValue={(value) => setTrainerVerificationChecks((previous) => ({ ...previous, originalSighted: value }))} />
                    <Checkbox label='Document expiry and status requirements checked' value={trainerVerificationChecks.documentStatusChecked} setValue={(value) => setTrainerVerificationChecks((previous) => ({ ...previous, documentStatusChecked: value }))} />
                    <Checkbox label='Student identity matches the selected EOI pathway' value={trainerVerificationChecks.identityMatched} setValue={(value) => setTrainerVerificationChecks((previous) => ({ ...previous, identityMatched: value }))} />
                </>
            )}

            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}

            {(!trainerMode || !hideTrainerDecisionActions) && <div style={{ display: 'flex', marginTop: '20px' }}>
                {trainerMode ? (
                    <>
                        <button style={{ backgroundColor: '#b42318' }} onClick={() => handleSubmit('cannot_verify')}>
                            {submitState === 'submitting' ? 'Saving...' : 'ID Cannot Be Verified'}
                        </button>
                        <button style={{ marginLeft: 'auto', display: 'flex', backgroundColor: '#1f7a1f', opacity: Object.values(trainerVerificationChecks).every(Boolean) ? 1 : 0.5, cursor: Object.values(trainerVerificationChecks).every(Boolean) ? 'pointer' : 'not-allowed' }} onClick={() => handleSubmit('verified')}>
                            {submitState === 'submitting' ? 'Saving...' : 'ID Verified'}
                        </button>
                    </>
                ) : (
                    <button style={{ marginLeft: 'auto', display: 'flex' }} onClick={() => handleSubmit()}>
                        {submitState === 'submitting' ? 'Saving...' : submitState === 'saved' ? 'Saved' : 'Submit'}
                    </button>
                )}
            </div>}
        </div>
    );
};

export default QldEoiForm;
